/**
 * Brand asset shapes (`brand_assets`, packages/core/src/db/schema/assets.ts) as read by the UI.
 * `assets.list` may return a bare array or `{assets|items: [...]}` — normalised here.
 */
import type { CSSProperties } from "react";
import { pickArray } from "@/api/types";

export const ASSET_KINDS = ["logo", "icon", "mascot", "product", "screenshot", "lifestyle", "ugc", "font", "other"] as const;
export type AssetKind = (typeof ASSET_KINDS)[number];

export const ASSET_KIND_LABEL: Record<AssetKind, string> = {
  logo: "Logos",
  icon: "Icons",
  mascot: "Mascots",
  product: "Products",
  screenshot: "Screenshots",
  lifestyle: "Lifestyle",
  ugc: "UGC",
  font: "Fonts",
  other: "Other",
};

export const ASSET_KIND_SINGULAR: Record<AssetKind, string> = {
  logo: "Logo",
  icon: "App icon",
  mascot: "Mascot",
  product: "Product",
  screenshot: "Screenshot",
  lifestyle: "Lifestyle",
  ugc: "UGC",
  font: "Font",
  other: "Other",
};

export const ASSET_SOURCE_LABEL: Record<string, string> = {
  upload: "Uploaded",
  site: "Website",
  app_store: "App Store",
  generated: "Generated",
  ad_account: "Ad account",
};

export interface BrandAsset {
  id: string;
  kind: AssetKind;
  name: string;
  mediaType: "image" | "video" | "font";
  url: string;
  source: string;
  sourceUrl: string | null;
  width: number | null;
  height: number | null;
  hasTransparency: boolean | null;
  tags: string[];
  description: string | null;
  favorite: boolean;
  archived: boolean;
  createdAt: string | null;
}

const s = (v: unknown) => (typeof v === "string" && v ? v : null);
const n = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);

export function normalizeAsset(v: unknown): BrandAsset | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const id = s(o.id);
  const url = s(o.url) ?? s(o.publicUrl);
  if (!id || !url) return null;
  const kind = (ASSET_KINDS as readonly string[]).includes(String(o.kind)) ? (o.kind as AssetKind) : "other";
  const mt = o.mediaType === "video" || o.mediaType === "font" ? o.mediaType : kind === "font" ? "font" : /\.(mp4|mov|webm)(\?|$)/i.test(url) ? "video" : "image";
  return {
    id,
    kind,
    name: s(o.name) ?? "",
    mediaType: mt,
    url,
    source: s(o.source) ?? "upload",
    sourceUrl: s(o.sourceUrl),
    width: n(o.width),
    height: n(o.height),
    hasTransparency: typeof o.hasTransparency === "boolean" ? o.hasTransparency : null,
    tags: Array.isArray(o.tags) ? o.tags.filter((t): t is string => typeof t === "string") : [],
    description: s(o.description),
    favorite: o.favorite === true,
    archived: o.archived === true,
    createdAt: s(o.createdAt),
  };
}

export function normalizeAssets(v: unknown): BrandAsset[] {
  return pickArray(v, "assets")
    .map(normalizeAsset)
    .filter((a): a is BrandAsset => !!a);
}

export function assetLabel(a: BrandAsset): string {
  return a.name || ASSET_KIND_SINGULAR[a.kind];
}

/** Light checkerboard behind transparent PNGs (PlayAd Brand Assets look). */
export const CHECKERBOARD: CSSProperties = {
  backgroundColor: "#f4f4f6",
  backgroundImage: "repeating-conic-gradient(#e6e6ea 0% 25%, transparent 0% 50%)",
  backgroundSize: "16px 16px",
};

/** Transparent-friendly kinds render "contain" on the checkerboard; photos fill the tile. */
export function showsOnCheckerboard(a: BrandAsset): boolean {
  if (a.mediaType !== "image") return false;
  if (a.hasTransparency) return true;
  return a.kind === "logo" || a.kind === "icon" || a.kind === "product" || /\.png(\?|$)|\.svg(\?|$)/i.test(a.url);
}

/** Guess a kind for an uploaded file (user can change it before import). */
export function guessKind(file: File, dims?: { width: number; height: number } | null): AssetKind {
  const name = file.name.toLowerCase();
  if (/\.(ttf|otf|woff2?)$/.test(name) || file.type.startsWith("font/")) return "font";
  if (/logo|wordmark|brandmark/.test(name)) return "logo";
  if (/icon/.test(name)) return "icon";
  if (/screen|screenshot|app-?store/.test(name)) return "screenshot";
  if (file.type.startsWith("video/")) return "ugc";
  if (/ugc|creator/.test(name)) return "ugc";
  if (dims && dims.height / Math.max(1, dims.width) > 1.7) return "screenshot";
  if (/lifestyle|model|person|people/.test(name)) return "lifestyle";
  return "product";
}
